import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindShow, bindText, html, list } from "../lib/bind";
import { daysSince, formatDate } from "../lib/date";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { type ActivityEntry, type Dashboard, getDashboard } from "../services/dashboard";
import { session } from "../stores/session";

const ACTION_LABELS: Record<string, string> = {
  "invoice.issued": "Issued",
  "invoice.voided": "Voided",
  "invoice.pdf_regenerated": "PDF regenerated for",
  "invoice.sent": "Sent",
  "invoice.send_failed": "Send failed for",
  "payment.created": "Payment received on",
  "payment.updated": "Payment updated on",
  "payment.deleted": "Payment deleted from",
};

function activityText(entry: ActivityEntry): string {
  const verb = ACTION_LABELS[entry.action] ?? entry.action;
  const subject = entry.invoice_number ?? "invoice";
  const who = entry.client_name ? ` — ${entry.client_name}` : "";
  const amount =
    entry.amount !== null ? ` (${formatMoney(entry.amount, entry.currency ?? "GBP")})` : "";
  return `${verb} ${subject}${who}${amount}`;
}

export function homeView(): Node {
  const dashboard = signal<Dashboard | null>(null);
  const activity = signal<ActivityEntry[]>([]);
  const loadError = signal("");

  if (session.companyId.value) {
    getDashboard()
      .then((result) => {
        dashboard.value = result;
        activity.value = result.recent_activity;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load";
      });
  } else {
    // Fresh login before any company exists/is selected — the switcher (shell) picks
    // one and hard-reloads; showing a hint beats firing a guaranteed-400 request.
    loadError.value = "Create or select a company to see your dashboard.";
  }

  const outstandingText = computed(() =>
    dashboard.value ? formatMoney(dashboard.value.outstanding_total) : "—",
  );
  const overdueText = computed(() => {
    if (!dashboard.value) return "—";
    if (dashboard.value.overdue_count === 0) return "None";
    return `${String(dashboard.value.overdue_count)} · ${formatMoney(dashboard.value.overdue_total)}`;
  });
  const draftsText = computed(() =>
    dashboard.value ? String(dashboard.value.draft_count) : "—",
  );
  const ytdText = computed(() =>
    dashboard.value ? formatMoney(dashboard.value.ytd_invoiced) : "—",
  );
  const showVatTile = computed(() => dashboard.value?.vat_current_period_end != null);
  const vatText = computed(() => {
    const d = dashboard.value;
    if (!d?.vat_estimated_box1 || !d.vat_current_period_end) return "—";
    // Estimate only — not yet locked, so this is a live projection, not a filed figure
    // (04-frontend.md: "current VAT period progress + estimated Box 1").
    return `${formatMoney(d.vat_estimated_box1)} (period ending ${formatDate(d.vat_current_period_end)})`;
  });
  const uncategorisedText = computed(() =>
    dashboard.value ? String(dashboard.value.uncategorised_txn_count) : "—",
  );

  // Instance-wide, so it appears whichever company is selected — a stopped backup job is
  // not one company's problem. The server owns the 48 h threshold; this only phrases it.
  const backupWarning = computed(() => {
    const d = dashboard.value;
    if (!d?.backup_stale) return "";
    if (!d.backup_last_at) {
      return "No verified backup exists yet — the nightly backup job has never completed.";
    }
    const days = daysSince(d.backup_last_at);
    const age = days === null ? "" : ` — ${String(days)} days ago`;
    return `Backups are stale. Last verified backup: ${formatDate(d.backup_last_at)}${age}.`;
  });
  const showBackupWarning = computed(() => backupWarning.value !== "");

  const loaded = computed(() => dashboard.value !== null);
  const showNoClients = computed(() => loaded.value && dashboard.value?.client_count === 0);
  const showNoInvoices = computed(
    () =>
      loaded.value &&
      (dashboard.value?.client_count ?? 0) > 0 &&
      dashboard.value?.invoice_count === 0,
  );
  const showActivityCard = computed(() => loaded.value && activity.value.length > 0);

  const fragment = html`<div class="view">
    <div class="page-header">
      <a href="/invoices/new" data-link class="btn btn-primary">New invoice</a>
    </div>
    <p class="form-error"></p>
    <div class="banner banner-warning backup-banner" role="status"></div>

    <div class="card-grid">
      <div class="card">
        <div class="card-label">Outstanding</div>
        <span class="card-value tile-outstanding"></span>
      </div>
      <div class="card">
        <div class="card-label">Overdue</div>
        <span class="card-value tile-overdue"></span>
      </div>
      <div class="card">
        <div class="card-label">Drafts awaiting issue</div>
        <span class="card-value tile-drafts"></span>
      </div>
      <div class="card">
        <div class="card-label">Invoiced this year (ex VAT)</div>
        <span class="card-value tile-ytd"></span>
      </div>
      <div class="card vat-tile-card">
        <div class="card-label">Estimated VAT (current period)</div>
        <span class="card-value tile-vat"></span>
      </div>
      <a href="/banking" data-link class="card tile-link">
        <div class="card-label">Uncategorised transactions</div>
        <span class="card-value tile-uncategorised"></span>
      </a>
    </div>

    <div class="card empty-state no-clients-card">
      <p>No clients yet — add your first client to start invoicing.</p>
      <div class="form-actions">
        <a href="/clients/new" data-link class="btn btn-primary">Add your first client</a>
      </div>
    </div>
    <div class="card empty-state no-invoices-card">
      <p>You have clients but no invoices yet.</p>
      <div class="form-actions">
        <a href="/invoices/new" data-link class="btn btn-primary">Create your first invoice</a>
      </div>
    </div>

    <div class="card activity-card">
      <div class="card-label">Recent activity</div>
      <ul class="activity-list"></ul>
    </div>
  </div>`;

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const outstandingEl = fragment.querySelector<HTMLElement>(".tile-outstanding");
  const overdueEl = fragment.querySelector<HTMLElement>(".tile-overdue");
  const draftsEl = fragment.querySelector<HTMLElement>(".tile-drafts");
  const ytdEl = fragment.querySelector<HTMLElement>(".tile-ytd");
  const vatTileCard = fragment.querySelector<HTMLElement>(".vat-tile-card");
  const vatEl = fragment.querySelector<HTMLElement>(".tile-vat");
  const noClientsCard = fragment.querySelector<HTMLElement>(".no-clients-card");
  const noInvoicesCard = fragment.querySelector<HTMLElement>(".no-invoices-card");
  const activityCard = fragment.querySelector<HTMLElement>(".activity-card");
  const activityList = fragment.querySelector<HTMLUListElement>(".activity-list");
  const uncategorisedEl = fragment.querySelector<HTMLElement>(".tile-uncategorised");
  const backupBanner = fragment.querySelector<HTMLElement>(".backup-banner");

  if (
    !errorEl ||
    !outstandingEl ||
    !overdueEl ||
    !draftsEl ||
    !ytdEl ||
    !vatTileCard ||
    !vatEl ||
    !noClientsCard ||
    !noInvoicesCard ||
    !activityCard ||
    !activityList ||
    !uncategorisedEl ||
    !backupBanner
  ) {
    throw new Error("dashboard markup missing expected elements");
  }

  bindErrorText(errorEl, loadError);
  bindText(backupBanner, backupWarning);
  bindShow(backupBanner, showBackupWarning);
  bindText(outstandingEl, outstandingText);
  bindText(overdueEl, overdueText);
  bindText(draftsEl, draftsText);
  bindText(uncategorisedEl, uncategorisedText);
  bindShow(vatTileCard, showVatTile);
  bindText(vatEl, vatText);
  bindText(ytdEl, ytdText);
  bindShow(noClientsCard, showNoClients);
  bindShow(noInvoicesCard, showNoInvoices);
  bindShow(activityCard, showActivityCard);

  currentScope().effect(() => {
    overdueEl.classList.toggle("tile-alert", (dashboard.value?.overdue_count ?? 0) > 0);
  });

  list(
    activityList,
    activity,
    (entry) => entry.id,
    (entry) => {
      const row = html`<li class="activity-row">
        <span class="activity-when">${formatDate(entry.at)}</span>
        ${
          entry.invoice_id
            ? html`<a href="/invoices/${entry.invoice_id}/view" data-link>${activityText(entry)}</a>`
            : html`<span>${activityText(entry)}</span>`
        }
      </li>`.firstElementChild;
      if (!row) throw new Error("activity row template produced no element");
      return row;
    },
  );

  return fragment;
}
